import { useCallback, useEffect, useRef, useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  buscarConciliacao,
  casarLinha,
  descasarLinha,
  ignorarLinha,
  importarExtrato,
  reais,
  type LinhaDoPainel,
  type Painel,
} from '../lib/financeiro';
import { useConfirmacao } from './Confirmacao';
import { Alerta, Botao, Cartao, Etiqueta } from './Interface';

export default function FinanceiroConciliacao({ ano, mes }: { ano: number; mes: number }) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      setPainel(await buscarConciliacao(ano, mes));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a conciliação.');
    }
  }, [ano, mes]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * O OFX e texto: o navegador le o arquivo e manda o conteudo. Nao precisa do
   * caminho de upload binario — nao ha arquivo parcial em disco para limpar.
   */
  async function enviarArquivo(arquivo: File) {
    if (arquivo.size > 2_000_000) {
      setErro('Arquivo maior que 2 MB. Exporte um periodo menor.');
      return;
    }
    setOcupado(true);
    setErro(null);
    setAviso(null);
    try {
      const conteudo = await arquivo.text();
      const r = await importarExtrato(arquivo.name, conteudo);
      setAviso(
        `${r.importadas} transacao(oes) importada(s), ${r.casadas} casada(s) automaticamente` +
          (r.repetidas ? `, ${r.repetidas} ja estavam aqui.` : '.'),
      );
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui importar o extrato.');
    } finally {
      setOcupado(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  async function acao(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      setAviso(mensagem);
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui concluir.');
    } finally {
      setOcupado(false);
    }
  }

  if (erro && !painel) return <Alerta>{erro}</Alerta>;
  if (!painel) return <p className="text-sm text-tinta-400">Carregando...</p>;

  const pendentes = painel.linhas.filter((l) => !l.lancamentoId && !l.ignorada);
  const conciliadas = painel.linhas.filter((l) => l.lancamentoId);
  const ignoradas = painel.linhas.filter((l) => l.ignorada && !l.lancamentoId);

  return (
    <div className="space-y-4">
      {dialogo}
      {erro && <Alerta>{erro}</Alerta>}
      {aviso && <Alerta tipo="ok">{aviso}</Alerta>}

      <Cartao>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-tinta-900">Extrato do banco</h2>
            <p className="mt-1 text-sm text-tinta-500">
              Baixe o .ofx no aplicativo do banco e envie aqui. Importar o mesmo arquivo duas vezes
              nao duplica nada.
            </p>
          </div>
          <input
            ref={entrada}
            type="file"
            accept=".ofx,.OFX,text/plain"
            disabled={ocupado || !painel.podeEditar}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo) void enviarArquivo(arquivo);
            }}
            className="text-sm text-tinta-600 file:mr-3 file:rounded-xl file:border-0 file:bg-tinta-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
          />
        </div>
        <div className="mt-3 flex gap-4 text-sm text-tinta-400">
          <span>{painel.totalConciliadas} conciliada(s)</span>
          <span>{painel.totalPendentes} pendente(s)</span>
        </div>
      </Cartao>

      <Cartao>
        <h2 className="mb-3 font-semibold text-tinta-900">
          No banco, ainda nao no caixa{' '}
          <span className="font-normal text-tinta-400">({pendentes.length})</span>
        </h2>
        {pendentes.length === 0 ? (
          <p className="text-sm text-tinta-400">Nada pendente neste mês.</p>
        ) : (
          <ul className="space-y-3">
            {pendentes.map((l) => (
              <LinhaPendente
                key={l.id}
                linha={l}
                podeEditar={painel.podeEditar}
                ocupado={ocupado}
                aoCasar={(lancamentoId) => acao(() => casarLinha(l.id, lancamentoId), 'Conciliado.')}
                aoIgnorar={async () => {
                  const ok = await confirmar({
                    titulo: 'Ignorar esta linha?',
                    texto: 'Ela continua no extrato, so sai da fila de pendencias.',
                    confirmar: 'Ignorar',
                  });
                  if (ok) await acao(() => ignorarLinha(l.id, true), 'Linha ignorada.');
                }}
              />
            ))}
          </ul>
        )}
      </Cartao>

      <Cartao>
        <h2 className="mb-3 font-semibold text-tinta-900">
          No caixa, nao no extrato{' '}
          <span className="font-normal text-tinta-400">({painel.soNoCaixa.length})</span>
        </h2>
        {painel.soNoCaixaEmEspecie > 0 && (
          <p className="mb-2 text-xs text-tinta-400">
            {painel.soNoCaixaEmEspecie} lancamento(s) em especie fora desta lista — dinheiro vivo
            nao passa pelo banco.
          </p>
        )}
        {painel.soNoCaixa.length === 0 ? (
          <p className="text-sm text-tinta-400">Tudo do caixa aparece no extrato.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {painel.soNoCaixa.map((l) => (
              <li key={l.id} className="flex justify-between gap-3">
                <span className="min-w-0 truncate text-tinta-600">
                  {l.dia.slice(8, 10)}/{l.dia.slice(5, 7)} · {l.descricao}{' '}
                  <span className="text-tinta-400">({l.forma})</span>
                </span>
                <span className="shrink-0 tabular-nums text-tinta-900">
                  {reais(l.valorCentavos)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {conciliadas.length > 0 && (
        <Cartao>
          <h2 className="mb-3 font-semibold text-tinta-900">
            Conciliadas <span className="font-normal text-tinta-400">({conciliadas.length})</span>
          </h2>
          <ul className="space-y-1 text-sm">
            {conciliadas.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-tinta-600">
                  {l.dia.slice(8, 10)}/{l.dia.slice(5, 7)} · {l.descricao}
                  <span className="text-tinta-400"> = {l.lancamentoDescricao}</span>
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="tabular-nums text-tinta-900">{reais(l.valorCentavos)}</span>
                  {painel.podeEditar && (
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => void acao(() => descasarLinha(l.id), 'Desfeito.')}
                      className="text-xs text-tinta-400 hover:text-tinta-700"
                    >
                      desfazer
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {ignoradas.length > 0 && (
        <Cartao>
          <h2 className="mb-3 font-semibold text-tinta-900">
            Ignoradas <span className="font-normal text-tinta-400">({ignoradas.length})</span>
          </h2>
          <ul className="space-y-1 text-sm">
            {ignoradas.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-tinta-400">
                  {l.dia.slice(8, 10)}/{l.dia.slice(5, 7)} · {l.descricao}
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="tabular-nums text-tinta-600">{reais(l.valorCentavos)}</span>
                  {painel.podeEditar && (
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => void acao(() => ignorarLinha(l.id, false), 'De volta a fila.')}
                      className="text-xs text-tinta-400 hover:text-tinta-700"
                    >
                      trazer de volta
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}
    </div>
  );
}

function LinhaPendente({
  linha,
  podeEditar,
  ocupado,
  aoCasar,
  aoIgnorar,
}: {
  linha: LinhaDoPainel;
  podeEditar: boolean;
  ocupado: boolean;
  aoCasar: (lancamentoId: number) => Promise<void>;
  aoIgnorar: () => Promise<void>;
}) {
  return (
    <li className="rounded-xl border border-tinta-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm text-tinta-900">{linha.descricao || 'Sem descrição'}</p>
          <p className="text-xs text-tinta-400">
            {linha.dia.slice(8, 10)}/{linha.dia.slice(5, 7)} · {linha.banco} {linha.conta}
          </p>
        </div>
        <span className="shrink-0 tabular-nums font-semibold text-tinta-900">
          {reais(linha.valorCentavos)}
        </span>
      </div>

      {linha.sugestoes.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-tinta-100 pt-2">
          <p className="text-xs text-tinta-500">
            {linha.sugestoes.length === 1 ? 'Parece ser este lancamento:' : 'Qual destes e?'}
          </p>
          {linha.sugestoes.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-tinta-600">
                {s.dia.slice(8, 10)}/{s.dia.slice(5, 7)} · {s.descricao}
                {s.diasDeDiferenca > 0 && (
                  <Etiqueta className="ml-2 bg-tinta-100 text-tinta-600">
                    {s.diasDeDiferenca}d de diferenca
                  </Etiqueta>
                )}
              </span>
              {podeEditar && (
                <Botao
                  variante="secundario"
                  className="shrink-0 px-3 py-1.5 text-xs"
                  disabled={ocupado}
                  onClick={() => void aoCasar(s.id)}
                >
                  E este
                </Botao>
              )}
            </div>
          ))}
        </div>
      )}

      {podeEditar && (
        <div className="mt-3">
          <button
            type="button"
            disabled={ocupado}
            onClick={() => void aoIgnorar()}
            className="text-xs text-tinta-400 hover:text-tinta-700"
          >
            ignorar esta linha
          </button>
        </div>
      )}
    </li>
  );
}
